import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { MessageCircleQuestion, Copy, RefreshCw } from 'lucide-react';
import {
  getAiQuestionSettings,
  saveAiQuestionSettings,
  rotateAiQuestionKey,
  AiQuestionSetting,
} from '../../../api/payment/ai-question.api';

// "Hỏi đáp AI trên live": người xem trả Ca gửi câu hỏi cho streamer đang live, AI viết câu
// trả lời, streamer duyệt hai bước rồi phát lên live qua bảng điều khiển riêng.

const CONTROL_URL = '/hoi-dap-ai/dieu-khien';

interface FormState {
  enabled: boolean;
  priceCa: string;
}

const toForm = (s: AiQuestionSetting): FormState => ({
  enabled: s.enabled,
  priceCa: `${s.priceCa}`,
});

const AiQuestionSettingBlock: React.FC<{ userId?: string }> = ({ userId }) => {
  const { origin } = window.location;
  const [setting, setSetting] = useState<AiQuestionSetting | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    setLoadError(false);
    getAiQuestionSettings()
      .then((res) => {
        setSetting(res);
        setForm(toForm(res));
      })
      .catch(() => {
        setLoadError(true);
        toast.error('Không tải được cài đặt hỏi đáp AI.');
      });
  };

  useEffect(() => {
    if (!userId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const overlayUrl = `${origin}/hoi-dap-screen/${userId}?key=${setting?.overlayKey || ''}`;

  const handleSave = () => {
    if (saving || !form) return; // chặn bấm hai lần
    const priceCa = Math.round(Number(form.priceCa));
    if (!Number.isFinite(priceCa) || priceCa <= 0) {
      toast.error('Giá mỗi câu hỏi không hợp lệ.');
      return;
    }
    setSaving(true);
    saveAiQuestionSettings({ enabled: form.enabled, priceCa })
      .then((res) => {
        setSetting(res);
        setForm(toForm(res));
        toast.success('Đã lưu cài đặt hỏi đáp AI');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Lưu cài đặt thất bại');
      })
      .finally(() => setSaving(false));
  };

  const handleRotateKey = () => {
    if (rotating) return;
    if (!window.confirm('Đổi khoá phần chèn? Link cũ sẽ ngưng hoạt động ngay.')) return;
    setRotating(true);
    rotateAiQuestionKey()
      .then((res) => {
        setSetting(res);
        toast.success('Đã đổi khoá phần chèn');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Đổi khoá thất bại');
      })
      .finally(() => setRotating(false));
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(overlayUrl);
      toast.success('Đã sao chép link');
    } catch {
      toast.error('Trình duyệt chặn sao chép, hãy bôi đen link rồi tự chép lại.');
    }
  };

  return (
    <div className="ui-card mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full ui-heading font-semibold text-[22px] leading-[27px]">
        <MessageCircleQuestion size={20} className="text-blue-600" />
        Hỏi đáp AI trên live
      </p>

      <div className="w-full flex flex-col gap-3 ui-surface-2 p-3">
        <p className="text-sm ui-muted">
          Người xem trả Ca để gửi câu hỏi, AI viết câu trả lời; bạn duyệt rồi phát lên live. Chỉ
          trừ Ca của người hỏi lúc bạn bấm phát lên live.
        </p>

        {loadError && !setting && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-3">
            <span className="text-sm text-red-600 dark:text-red-400">
              Không tải được cài đặt hỏi đáp AI.
            </span>
            <button
              type="button"
              onClick={load}
              className="shrink-0 rounded-lg border border-red-300 dark:border-red-700 px-3 py-1 text-sm text-red-600 dark:text-red-400"
            >
              Thử lại
            </button>
          </div>
        )}

        {form && (
          <>
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm((f) => (f ? { ...f, enabled: e.target.checked } : f))}
                className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
              />
              <span className="text-sm ui-text">
                Bật hỏi đáp AI
                <span className="block text-xs ui-muted">
                  Tắt thì trang cá nhân của bạn ẩn khối gửi câu hỏi, bảng điều khiển cũng tạm dừng
                  duyệt/viết lại/phát lên live. Câu hỏi đang chờ sẽ đợi tới khi bạn bật lại, quá
                  hạn thì tự hết hạn chứ không mất.
                </span>
              </span>
            </label>

            <div className="flex flex-col gap-1 max-w-xs">
              <label htmlFor="ai-question-price" className="text-sm ui-heading">
                Giá mỗi câu hỏi (Ca)
              </label>
              <input
                id="ai-question-price"
                type="number"
                min={1}
                step={1}
                value={form.priceCa}
                onChange={(e) => setForm((f) => (f ? { ...f, priceCa: e.target.value } : f))}
                placeholder="VD: 10000"
                className="ui-input h-11 w-full px-3 text-base"
              />
            </div>

            <div className="flex flex-col gap-1">
              <h2 className="text-sm ui-heading">
                Link phần chèn (Thêm nguồn Trình duyệt (Browser) trong phần mềm phát live và dán
                link này — ví dụ OBS)
              </h2>
              <div className="flex items-center gap-2">
                <div className="flex h-12 min-w-0 flex-1 items-center ui-input px-3 text-base">
                  <span className="w-full truncate">{overlayUrl}</span>
                </div>
                <button
                  type="button"
                  onClick={copyLink}
                  title="Sao chép link"
                  className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
                >
                  <Copy size={16} />
                  Sao chép
                </button>
              </div>
              <button
                type="button"
                onClick={handleRotateKey}
                disabled={rotating}
                className="mt-1 flex w-fit items-center gap-1 rounded-lg border ui-divider px-3 py-2 text-sm ui-text disabled:opacity-60"
              >
                <RefreshCw size={14} />
                {rotating ? 'Đang đổi…' : 'Đổi khoá'}
              </button>
              <p className="text-xs ui-muted">
                Đổi khoá nếu link cũ bị lộ — link cũ ngưng hoạt động ngay.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <h2 className="text-sm ui-heading">Bảng điều khiển hỏi đáp AI</h2>
              <div className="flex items-center gap-2">
                <div className="flex h-12 min-w-0 flex-1 items-center ui-input px-3 text-base">
                  <span className="w-full truncate">{`${origin}${CONTROL_URL}`}</span>
                </div>
                <a
                  href={CONTROL_URL}
                  className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
                >
                  Mở
                </a>
              </div>
              <p className="text-xs ui-muted">
                Mở trang này để duyệt câu hỏi, xem câu trả lời của AI và phát lên live.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Đang lưu…' : 'Lưu cài đặt'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AiQuestionSettingBlock;
